// The words a person reads for the app's codes (UI/UX audit, 31 Oct 2026, UX-17 and UX-18).
//
// WHY ONE MODULE. Grant codes, check kinds, run statuses and the API's flags reached the screens
// as the database spells them: "needs a current METER_ADMIN authorisation", a picker of bare codes
// with the retired ARTWORK_SIGNOFF in it, "When: FIRST_OFF", "Door: SPA", "no evidence on fail".
// Each page had started its own translation (the draft editor's CHECK_KINDS, SpecVersion's
// whenText), and two copies of a label drift exactly as two copies of a vocabulary did (rule 14).
//
// ⚠ NOT RULE 14 MIRRORS, EXCEPT FoundBy. Every map below is a `Record<Union, …>` keyed by a union
// that is ALREADY a checked mirror (lib/types.ts AuthAction, CompetencyActivity, CheckKind,
// ObligationStatus - see MIRRORS in api/src/__tests__/vocabulary.test.ts). The compiler refuses a
// map that misses a member or names one the union lacks, so a widened constraint fails the mirror
// test first and then fails the build here until it is labelled. `FoundBy` is declared HERE (the
// SPA had it only as `string`), so it is a mirror of CK_CofA_FoundBy and is listed in MIRRORS.
//
// Keep the CODE beside the words where a person must type it or match it in another system: a
// grant picker says "Verify start-up checks (VERIFY)" because the grant register, the gateway and
// a conversation with QA all name the code.
import type {
  Aggregation,
  AuthAction,
  CheckKind,
  ClientKind,
  CompetencyActivity,
  IngestVerdict,
  ObligationStatus,
  RunStatus,
  SpecStatus,
  Verdict,
} from './types';

/** A grant action: what it lets a person do, and one line saying what that means. */
export const ACTION_LABEL: Record<AuthAction, { label: string; meaning: string }> = {
  CAPTURE: { label: 'Record line checks', meaning: 'Take and record readings and start-up checks at the line.' },
  VERIFY: { label: 'Verify start-up checks', meaning: "Sign off somebody else's start-up check. Never their own." },
  RELEASE: {
    label: 'Release and decide',
    meaning: 'QA: decide certificates, inspect goods at the dock, close inspection failures and release holds.',
  },
  RECEIVE: { label: 'Inspect vehicles at the dock', meaning: 'Record the vehicle inspection when a delivery arrives.' },
  SPEC_APPROVE: { label: 'Approve specifications', meaning: 'Sign a specification draft into force, and approve issuer and material names.' },
  CONCESSION: { label: 'Grant concessions', meaning: 'Accept material outside its specification under a concession.' },
  ASSESS: { label: 'Assess competency', meaning: "Record a person's competency assessment." },
  METER_ADMIN: { label: 'Manage meters', meaning: 'Record calibrations and take meters in and out of service.' },
  AUTH_ADMIN: { label: 'Grant authorisations', meaning: 'Make and end grants on the Authority page.' },
  ARTWORK_SIGNOFF: { label: 'Artwork sign-off (retired)', meaning: 'Retired 26 Oct 2026: artwork is checked in MAGIC now.' },
  COMPLAINT_TRIAGE: {
    label: 'Consumer complaints',
    meaning: 'Record, take and acknowledge consumer complaints, and close Low and Medium cases QA has found on.',
  },
  DISPOSITION_RD: {
    label: 'Authorise out-of-spec release (R&D)',
    meaning: 'R&D: authorise releasing or reworking held product outside its specification, after a risk assessment.',
  },
  DISPOSITION_MFG: {
    label: 'Co-sign holds (Manufacturing Manager)',
    meaning: 'Co-sign out-of-spec product to a customer, rework plans and holds escalated at a risk score of 10 or more.',
  },
  DISPOSITION_LOGISTICS: {
    label: 'Action disposals (Logistics)',
    meaning: 'Logistics Manager: action the disposal of rejected product, materials and packaging.',
  },
};

/**
 * Actions that still label the grants made before they retired but are no longer offered: not in
 * a picker and not a coverage question. `ACTIONS` on the Authority page stays complete, because it
 * is the rule 14 mirror of CK_Authorisation_Action; this set is what the screens filter by.
 */
export const RETIRED_ACTIONS: ReadonlySet<AuthAction> = new Set<AuthAction>(['ARTWORK_SIGNOFF']);

/** "Verify start-up checks". */
export const actionLabel = (a: AuthAction): string => ACTION_LABEL[a]?.label ?? a;
/** "Verify start-up checks (VERIFY)": where the code is matched elsewhere (the register, the gateway). */
export const actionWithCode = (a: AuthAction): string => (ACTION_LABEL[a] ? `${ACTION_LABEL[a].label} (${a})` : a);

/**
 * A competency activity, and the grant whose gate checks it (UX-17: the Activity picker showed no
 * pairing). `grant` is null where no gate in this app asks for the activity yet.
 */
export const ACTIVITY_LABEL: Record<CompetencyActivity, { label: string; grant: AuthAction | null }> = {
  CAPTURE: { label: 'Taking line checks', grant: 'CAPTURE' },
  VERIFY: { label: 'Verifying start-up checks', grant: 'VERIFY' },
  RECEIPT_INSPECT: { label: 'Judging supplier certificates', grant: 'RELEASE' },
  SPEC_APPROVE: { label: 'Approving specifications', grant: 'SPEC_APPROVE' },
  DISPOSITION: { label: 'Deciding held product', grant: null },
};

export const activityLabel = (a: CompetencyActivity): string => ACTIVITY_LABEL[a]?.label ?? a;
/** The picker's line: the words, the code, and the grant it stands behind. */
export function activityOption(a: CompetencyActivity): string {
  const v = ACTIVITY_LABEL[a];
  if (!v) return a;
  return v.grant ? `${v.label} (${a}), for ${actionLabel(v.grant)}` : `${v.label} (${a})`;
}

/** When a parameter is checked. The draft editor's words, now shared (UX-18). */
export const CHECK_KIND_LABEL: Record<CheckKind, string> = {
  FIRST_OFF: 'First-off only',
  ADHERENCE: 'In process only',
  BOTH: 'First-off and in process',
  READINESS: 'Start-up check',
  COFA: 'On the supplier certificate',
  RECEIPT: 'Tested at receipt',
};

/** How the samples are judged. The draft editor's words, now shared (UX-18). */
export const AGGREGATION_LABEL: Record<Aggregation, string> = {
  ALL: 'Every sample',
  MEAN: 'The mean',
  AQS: 'AQS (net weight)',
};

export const VERDICT_LABEL: Record<Verdict, string> = { IN: 'In spec', ACTION: 'Action', OUT: 'Out of spec' };
export const INGEST_VERDICT_LABEL: Record<IngestVerdict, string> = { ACCEPTED: 'Accepted', REJECTED: 'Refused' };

export const OBLIGATION_STATUS_LABEL: Record<ObligationStatus, string> = {
  OWED: 'Owed',
  AWAITING_RESULT: 'Awaiting result',
  AWAITING_VERIFY: 'Awaiting verify',
  MET: 'Met',
  MISSED: 'Missed',
  WAIVED: 'Waived',
};

export const RUN_STATUS_LABEL: Record<RunStatus, string> = { OPEN: 'Running', CLOSED: 'Closed' };
export const SPEC_STATUS_LABEL: Record<SpecStatus, string> = { DRAFT: 'Draft', APPROVED: 'Approved', SUPERSEDED: 'Superseded' };
/** Which door a reading came through. */
export const DOOR_LABEL: Record<ClientKind, string> = { SPA: 'App', GATEWAY: 'Chat (gateway)' };

/** ⚠ Mirrors `CK_CofA_FoundBy` (listed in MIRRORS): how a certificate was matched to its PO line. */
export type FoundBy = 'BATCH' | 'PO' | 'SO' | 'INVOICE' | 'OTHER';
export const FOUND_BY_LABEL: Record<FoundBy, string> = {
  BATCH: 'its lot (a received batch)',
  PO: 'PO number',
  SO: 'sales order',
  INVOICE: 'invoice number',
  OTHER: 'other means',
};
export const foundByLabel = (f: string): string => FOUND_BY_LABEL[f as FoundBy] ?? f;

/**
 * The verify queue's attention flags (api/src/tools/readiness.ts attentionOf), as sentences. An
 * API list, not a database one: a flag this map does not know is shown with its underscores
 * spaced, never dropped.
 */
const ATTENTION_LABEL: Record<string, string> = {
  failed: 'Answered No or out of spec',
  no_evidence_on_fail: 'Failed with no comment or photo',
  ccp: 'Critical control point (CCP)',
  oprp: 'Operational prerequisite (OPRP)',
  failed_last_run_on_line: 'Failed on the last run on this line',
  waived: 'Waived',
};
export const attentionLabel = (flag: string): string => ATTENTION_LABEL[flag] ?? flag.replace(/_/g, ' ');

/**
 * A stored reject reason is `code: sentence` (ingestGate.ts writes it so). The sentence is what a
 * person reads; the code goes in a tooltip for whoever matches it against the logs.
 */
export function rejectText(raw: string | null | undefined): { sentence: string; code: string | null } {
  if (!raw) return { sentence: '', code: null };
  const m = /^([a-z]+(?:_[a-z]+)+|[a-z]+):\s+([\s\S]+)$/.exec(raw);
  return m ? { sentence: m[2], code: m[1] } : { sentence: raw, code: null };
}

/**
 * A reason with its machine codes taken out, for display: "(attachment_ambiguous)" goes, and
 * "(UNMATCHED: what was read is kept)" keeps its words, "(what was read is kept)". The stored
 * reason is unchanged; put it in a `title` where the code still matters.
 */
export function withoutCodes(text: string): string {
  // A code is snake_case (lower or upper), or an upper-case word with a colon after it: "(PDF)" and
  // "(CCP)" are words a person reads and stay.
  return text
    .replace(/\s*\((?:[A-Z]+(?:_[A-Z]+)+|[a-z]+(?:_[a-z]+)+)(?::\s*([^)]*))?\)/g, (_m, rest: string | undefined) =>
      rest ? ` (${rest})` : '',
    )
    .replace(/\s*\([A-Z]{3,}:\s*([^)]*)\)/g, (_m, rest: string) => ` (${rest})`)
    .trim();
}
